import './defines';
import ButtonToggleComponent from 'components/button-toggle';
import ButtonToggleGroupComponent from 'components/button-toggle-group';
import SelectComponent from 'components/select';
import {
	EditorToolbarLayoutOption,
	EditorToolbarLayoutOptions,
	EditorToolbarLayouts,
	SelectSlotOptions,
	TemplateSet,
} from 'interfaces/app';
import { PageModel } from 'interfaces/project';
import { FontModule } from 'store';
import { mobile as mobileTools } from 'tools';
import {
	Component,
	Model,
	Prop,
	Vue,
	Watch,
} from 'vue-property-decorator';
import Template from './template.vue';

@Component({
	name: 'EditorLayoutSelectorView',
	components: {
		ButtonToggleComponent,
		ButtonToggleGroupComponent,
		SelectComponent,
	},
})
export default class EditorLayoutSelectorView extends Vue.extend(Template) {
	@Model(
		'change',
		{
			default: undefined,
			description: 'Defines the selected layout model',
			schema: 'TemplateSet',
			type: Object,
		},
	)
	public readonly value?: TemplateSet;

	@Prop({
		default: 1,
		description: 'Defines the number of columns in the editor layout selector',
		type: Number,
	})
	public readonly columns!: number;

	@Prop({
		description: 'Defines the available layouts for the active page model',
		required: true,
		schema: 'EditorToolbarLayouts',
		type: Array,
	})
	public readonly layouts!: EditorToolbarLayouts;

	@Prop({
		default: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla iaculis eu ligula et consequat. In hac habitasse platea dictumst. Sed felis eros, tristique vitae leo eget, ultricies convallis nulla. Ut ut arcu aliquam, mollis odio quis, faucibus lacus. Nulla nunc nisl, eleifend molestie velit eu, ultricies mollis nibh. Sed viverra ante id purus fermentum vestibulum. Sed lobortis gravida blandit. Curabitur vitae massa velit. Integer ut urna efficitur, dapibus risus vel, semper sapien. Nam ex tortor, ullamcorper vitae ultricies id, malesuada in lorem. Nulla nisl nunc, volutpat et lectus nec, elementum sodales elit. Maecenas iaculis, risus ac scelerisque egestas, nibh felis pretium mauris, sit amet hendrerit est sem id lectus. Nullam congue ante sed elit interdum, quis fermentum augue venenatis.\n\nVivamus molestie purus eu dui dapibus, vel semper arcu accumsan. Praesent porttitor ligula ante. Integer imperdiet efficitur gravida. Vestibulum non tellus dignissim, consequat est vitae, placerat dolor. Aliquam molestie finibus dui sed viverra. Phasellus elit sem, scelerisque non nunc non, elementum fermentum ex. Vestibulum elementum urna quis diam faucibus rhoncus. Sed sodales consequat dolor, quis maximus neque condimentum ut. Integer a posuere purus. Suspendisse lorem purus, efficitur eu nibh non, consequat eleifend quam. Aliquam semper lacinia congue. Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia curae;\n\nSuspendisse lacus odio, tempor nec pulvinar sit amet, commodo et nisi. Vestibulum id accumsan lorem. Vestibulum massa odio, porta quis facilisis et, elementum at leo. Maecenas dignissim turpis leo, at luctus massa tempor sed. Maecenas ullamcorper, diam vel posuere rutrum, nisl justo ultricies sem, et mattis purus ante sed lectus. Morbi turpis est, sodales sit amet nulla quis, ultricies blandit lacus. Pellentesque auctor sodales augue. Ut nec metus nec lacus scelerisque lobortis. Mauris euismod lectus libero, in luctus metus interdum',
		description: 'Defines the default text to display on the layout position (when type is text)',
		type: String,
	})
	public readonly layoutText!: string;

	@Prop({
		description: 'Defines the active page model',
		required: true,
		schema: 'PageModel',
		type: Object,
	})
	public readonly pageModel!: PageModel;

	@Prop({
		default: undefined,
		description: 'Defines the width of the each layout (which is used to calculate the layout scaling), if not provided, the available width will be used',
		type: Number,
	})
	public readonly width?: number;

	protected get computedLayoutStyles(): Partial<CSSStyleDeclaration> {
		const height = Math.floor(this.pageModel.height * this.scaling);
		const width = Math.floor(this.pageModel.width * this.scaling);

		return {
			height: `${height}px`,
			width: `${width}px`,
		};
	}

	protected get computedLayoutOptions(): EditorToolbarLayoutOption[] {
		return Object.values(this.layoutOptions);
	}

	protected get computedLayoutText(): (selectSlotOptions: SelectSlotOptions<EditorToolbarLayoutOption['value'][]>) => string {
		return (selectSlotOptions) => {
			const selectedLayouts: EditorToolbarLayoutOption[] = [];

			// eslint-disable-next-line no-restricted-syntax
			for (const layoutOption of selectSlotOptions.value) {
				selectedLayouts.push(this.layoutOptions[layoutOption]);
			}

			if (selectSlotOptions.count === 1) {
				return selectedLayouts[0].placeholder;
			}
			if (
				selectSlotOptions.count
				&& selectSlotOptions.count > 1
			) {
				return this.$t(
					'views.editorLayoutSelector.filters',
					{
						count: selectSlotOptions.count,
					},
				);
			}

			return this.$t('views.editorLayoutSelector.all.desktop');
		};
	}

	protected get computedStyles(): Partial<CSSStyleDeclaration> & Record<string, number | string> {
		return {
			'--columns': this.columns,
		};
	}

	protected get computedWidth(): number {
		if (this.width) {
			return this.width;
		}
		if (this.$el) {
			return this.$el.clientWidth;
		}

		return 0;
	}

	private get layoutOptions(): EditorToolbarLayoutOptions {
		const layoutOptions: EditorToolbarLayoutOptions = {} as EditorToolbarLayoutOptions;

		if (this.layouts?.length) {
			const onlyImages: EditorToolbarLayouts = [];
			const withTexts: EditorToolbarLayouts = [];
			const oneImage: EditorToolbarLayouts = [];
			const twoImages: EditorToolbarLayouts = [];
			const threeImages: EditorToolbarLayouts = [];
			const fourImages: EditorToolbarLayouts = [];
			const fivePlusImages: EditorToolbarLayouts = [];

			// eslint-disable-next-line no-restricted-syntax
			for (const layout of this.layouts) {
				const imagePositions = layout.positions.filter((position) => position.type === 'photo');
				const textPositions = layout.positions.filter((position) => position.type === 'text');

				if (imagePositions.length) {
					if (textPositions.length === 0) {
						onlyImages.push(layout);
					}

					if (imagePositions.length === 1) {
						oneImage.push(layout);
					} else if (imagePositions.length === 2) {
						twoImages.push(layout);
					} else if (imagePositions.length === 3) {
						threeImages.push(layout);
					} else if (imagePositions.length === 4) {
						fourImages.push(layout);
					} else {
						fivePlusImages.push(layout);
					}
				}
				if (textPositions.length) {
					withTexts.push(layout);
				}
			}

			if (onlyImages.length) {
				layoutOptions['only-images'] = {
					placeholder: this.$t('views.editorLayoutSelector.onlyImages'),
					templateSets: onlyImages,
					title: `${this.$t('views.editorLayoutSelector.onlyImages')} (${onlyImages.length})`,
					value: 'only-images',
				};
			}

			if (withTexts.length) {
				layoutOptions['with-texts'] = {
					placeholder: this.$t('views.editorLayoutSelector.withTexts'),
					templateSets: withTexts,
					title: `${this.$t('views.editorLayoutSelector.withTexts')} (${withTexts.length})`,
					value: 'with-texts',
				};
			}

			if (oneImage.length) {
				const imageText = this.$t(
					'views.editorLayoutSelector.image_interval',
					{
						postProcess: 'interval',
						count: 1,
					},
				);
				layoutOptions['one-image'] = {
					placeholder: imageText,
					templateSets: oneImage,
					title: `${imageText} (${oneImage.length})`,
					value: 'one-image',
				};
			}

			if (twoImages.length) {
				const imageText = this.$t(
					'views.editorLayoutSelector.image_interval',
					{
						postProcess: 'interval',
						count: 2,
					},
				);
				layoutOptions['two-images'] = {
					placeholder: imageText,
					templateSets: twoImages,
					title: `${imageText} (${twoImages.length})`,
					value: 'two-images',
				};
			}

			if (threeImages.length) {
				const imageText = this.$t(
					'views.editorLayoutSelector.image_interval',
					{
						postProcess: 'interval',
						count: 3,
					},
				);
				layoutOptions['three-images'] = {
					placeholder: imageText,
					templateSets: threeImages,
					title: `${imageText} (${threeImages.length})`,
					value: 'three-images',
				};
			}

			if (fourImages.length) {
				const imageText = this.$t(
					'views.editorLayoutSelector.image_interval',
					{
						postProcess: 'interval',
						count: 4,
					},
				);
				layoutOptions['four-images'] = {
					placeholder: imageText,
					templateSets: fourImages,
					title: `${imageText} (${fourImages.length})`,
					value: 'four-images',
				};
			}

			if (fivePlusImages.length) {
				const imageText = this.$t(
					'views.editorLayoutSelector.image_interval',
					{
						postProcess: 'interval',
						count: 5,
					},
				);
				layoutOptions['fiveplus-images'] = {
					placeholder: imageText,
					templateSets: fivePlusImages,
					title: `${imageText} (${fivePlusImages.length})`,
					value: 'fiveplus-images',
				};
			}
		}

		return layoutOptions;
	}

	protected get layoutsToShow(): EditorToolbarLayoutOption['templateSets'] {
		let layoutCategories: Array<keyof EditorToolbarLayoutOptions>;

		if (this.layoutCategories.length === 0) {
			layoutCategories = Object.keys(this.layoutOptions) as Array<keyof EditorToolbarLayoutOptions>;
		} else {
			layoutCategories = this.layoutCategories;
		}

		return layoutCategories
			.map((layoutCategory) => this.layoutOptions[layoutCategory].templateSets)
			.flat()
			.reduce(
				(templateSets, templateSet) => {
					if (!templateSets.find((templateSetFound) => templateSetFound.id === templateSet.id)) {
						templateSets.push(templateSet);
					}

					return templateSets;
				},
				[] as TemplateSet[],
			);
	}

	protected get scaling(): number {
		return this.computedWidth / this.pageModel.width;
	}

	private internalValue: TemplateSet | null = null;

	protected isMobile = mobileTools.isMobile;

	private isMobileUnwatch?: () => void;

	private layoutCategories: EditorToolbarLayoutOption['value'][] = [];

	protected beforeDestroy(): void {
		this.isMobileUnwatch?.();
	}

	protected created(): void {
		this.isMobileUnwatch = mobileTools.watch(() => {
			this.isMobile = mobileTools.isMobile;
		});
	}

	protected mounted(): void {
		if (!this.width) {
			this.$forceCompute('computedWidth');
			this.$forceCompute('scaling');
			this.$forceCompute('computedLayoutStyles');
		}
	}

	@Watch(
		'layouts',
		{
			deep: true,
			immediate: true,
		},
	)
	protected onLayoutsChange(): void {
		// eslint-disable-next-line no-restricted-syntax
		for (const layout of this.layouts) {
			// eslint-disable-next-line no-restricted-syntax
			for (const position of layout.positions) {
				if (position.type == 'text') {
					FontModule.loadModel(position.fontface);
				}
			}
		}
	}

	@Watch(
		'value',
		{
			immediate: true,
		},
	)
	protected onValueChange(): void {
		if (this.value?.id !== this.internalValue?.id) {
			this.internalValue = this.value || null;
		}
	}

	protected getPositionContent(position: EditorToolbarLayouts[number]['positions'][number]): string {
		if (position.type !== 'text') {
			return '';
		}

		return this.layoutText;
	}

	protected getPositionStyles(position: EditorToolbarLayouts[number]['positions'][number]): Partial<CSSStyleDeclaration> {
		const styles: Partial<CSSStyleDeclaration> = {
			height: `${Math.floor(position.height * this.scaling)}px`,
			left: `${Math.floor(position.x * this.scaling)}px`,
			top: `${Math.floor(position.y * this.scaling)}px`,
			width: `${Math.floor(position.width * this.scaling)}px`,
		};

		if (position.type === 'photo') {
			styles.backgroundColor = 'var(--neutral6-grey-fill)';
		}
		if (position.type === 'text') {
			const fontSizePx = (position.pointsize * 31496 / 15120) * this.scaling;
			styles.color = '#000000';
			styles.fontFamily = position.fontface;
			styles.fontSize = `${fontSizePx}px`;
			styles.lineHeight = `${fontSizePx * 1.25}px`;
			styles.textAlign = position.align.toLowerCase();

			if (position.fontitalic) {
				styles.fontStyle = 'italic';
			}
			if (position.fontbold) {
				styles.fontWeight = 'bold';
			}
		}

		return styles;
	}

	protected onAllLayoutsChange(): void {
		this.layoutCategories = [];
	}

	protected onChange(layoutId: EditorToolbarLayouts[number]['id']): void {
		this.internalValue = this.layouts.find((layout) => layout.id === layoutId) || null;

		if (this.internalValue?.id !== this.value?.id) {
			this.$emit(
				'change',
				this.internalValue,
			);
		}
	}

	protected onLayoutCategoryChange(
		layoutOption: EditorToolbarLayoutOption,
		value: boolean,
	): void {
		if (value) {
			this.layoutCategories.push(layoutOption.value);
		} else {
			this.layoutCategories.splice(
				this.layoutCategories.indexOf(layoutOption.value),
				1,
			);
		}
	}
}
